/**
 * Global Error Handler Composable
 *
 * Provides centralized error handling for the entire application.
 * Automatically shows user-friendly error messages using toasts and
 * navigates to appropriate error pages when needed.
 *
 * @example
 * import { useErrorHandler } from '@/Composables/useErrorHandler';
 *
 * const { handleError, handleAxiosError } = useErrorHandler();
 *
 * try {
 *   await someOperation();
 * } catch (error) {
 *   handleError(error);
 * }
 *
 * // Or with axios specifically:
 * axios.get('/api/data')
 *   .catch(handleAxiosError);
 */

import { router } from '@inertiajs/vue3';
import { useToast, showApiError } from '@/Composables/useToast';

/**
 * Main error handler composable
 */
export function useErrorHandler() {
    const toast = useToast();

    /**
     * Handle axios/HTTP errors
     * Shows appropriate error page or toast based on status code
     * @param {Object} error - Axios error object
     * @param {Object} options - Handler options
     * @param {boolean} options.showToast - Whether to show toast notification (default: true)
     * @param {boolean} options.navigateToErrorPage - Whether to navigate to error page for certain errors (default: true)
     * @param {Function} options.onError - Custom error callback
     */
    const handleAxiosError = (error, options = {}) => {
        const {
            showToast = true,
            navigateToErrorPage = true,
            onError = null,
        } = options;

        // Check if user is in the middle of logout
        // If so, suppress error alerts to avoid confusing the user
        const isLoggingOut = sessionStorage.getItem('is_logging_out') === 'true';

        // Log to console in development
        if (import.meta.env.DEV) {
            console.error('Axios Error:', error);
            if (isLoggingOut) {
                console.log('Suppressing error alert during logout');
            }
        }

        if (!error.response) {
            // Network error or request was never made
            if (showToast && !isLoggingOut) {
                toast.error(
                    'Unable to connect to the server. Please check your internet connection.',
                    'Network Error',
                    { duration: 7000 }
                );
            }

            if (onError) onError(error);
            return;
        }

        const status = error.response.status;
        const data = error.response.data;

        // Handle different status codes
        switch (status) {
            case 401:
                // Unauthorized - redirect to login
                // Don't show toast if user is logging out (they expect to be redirected)
                if (showToast && !isLoggingOut) {
                    toast.warning('Your session has expired. Please log in again.', 'Session Expired');
                }

                // Redirect to login after a short delay (unless already on login page or logging out)
                if (!isLoggingOut) {
                    setTimeout(() => {
                        if (!window.location.pathname.includes('/login')) {
                            window.location.href = '/login';
                        }
                    }, 1500);
                }
                break;

            case 403:
                // Forbidden
                if (navigateToErrorPage) {
                    router.visit('/errors/403', {
                        method: 'get',
                        data: { message: data.message || 'You do not have permission to access this resource.' }
                    });
                } else if (showToast) {
                    toast.error(
                        data.message || 'You do not have permission to access this resource.',
                        'Access Denied'
                    );
                }
                break;

            case 404:
                // Not Found
                if (navigateToErrorPage) {
                    router.visit('/errors/404', {
                        method: 'get',
                        data: { message: data.message || 'The requested resource was not found.' }
                    });
                } else if (showToast) {
                    toast.error(
                        data.message || 'The requested resource was not found.',
                        'Not Found'
                    );
                }
                break;

            case 419:
                // CSRF Token Mismatch / Session Expired
                // Don't show toast or auto-refresh if user is logging out
                if (showToast && !isLoggingOut) {
                    toast.warning(
                        'Your session has expired. Please refresh the page and try again.',
                        'Session Expired',
                        { duration: 8000 }
                    );
                }

                // Optionally auto-refresh after showing message (unless logging out)
                if (!isLoggingOut) {
                    setTimeout(() => {
                        window.location.reload();
                    }, 3000);
                }
                break;

            case 422:
                // Validation Error
                if (showToast) {
                    if (data.errors) {
                        // Laravel validation errors
                        const errorMessages = Object.values(data.errors).flat();
                        if (errorMessages.length === 1) {
                            toast.error(errorMessages[0], 'Validation Error');
                        } else if (errorMessages.length > 1) {
                            const message = `${errorMessages[0]} (and ${errorMessages.length - 1} more error${errorMessages.length - 1 > 1 ? 's' : ''})`;
                            toast.error(message, 'Validation Error', { duration: 7000 });
                        }
                    } else if (data.message) {
                        toast.error(data.message, 'Validation Error');
                    }
                }
                break;

            case 429:
                // Too Many Requests
                if (showToast) {
                    toast.warning(
                        data.message || 'Too many requests. Please slow down and try again later.',
                        'Rate Limit Exceeded',
                        { duration: 8000 }
                    );
                }
                break;

            case 500:
                // Internal Server Error
                if (navigateToErrorPage) {
                    router.visit('/errors/500', {
                        method: 'get',
                        data: {
                            message: import.meta.env.DEV
                                ? (data.message || 'An internal server error occurred.')
                                : 'Something went wrong on our end. Please try again later.'
                        }
                    });
                } else if (showToast) {
                    toast.error(
                        import.meta.env.DEV
                            ? (data.message || 'An internal server error occurred.')
                            : 'Something went wrong on our end. Please try again later.',
                        'Server Error',
                        { duration: 8000 }
                    );
                }
                break;

            case 503:
                // Service Unavailable
                if (navigateToErrorPage) {
                    router.visit('/errors/503', {
                        method: 'get',
                        data: { message: data.message || 'The service is temporarily unavailable.' }
                    });
                } else if (showToast) {
                    toast.error(
                        data.message || 'The service is temporarily unavailable. Please try again later.',
                        'Service Unavailable',
                        { duration: 8000 }
                    );
                }
                break;

            case 409:
                // Conflict - These are typically company context mismatches
                // The ValidateCompanyContext middleware handles redirects automatically
                // Don't show toast here to avoid duplicate/confusing messages
                // The middleware will redirect the user appropriately
                break;

            default:
                // Other errors (including 409 during logout)
                // Don't show toast if user is logging out
                if (showToast && !isLoggingOut) {
                    toast.error(
                        data.message || `An error occurred (${status})`,
                        'Error',
                        { duration: 7000 }
                    );
                }
        }

        // Call custom error handler if provided
        if (onError) {
            onError(error);
        }
    };

    /**
     * Handle general JavaScript errors
     * @param {Error|string} error - Error object or message
     * @param {Object} options - Handler options
     */
    const handleError = (error, options = {}) => {
        const {
            showToast = true,
            title = 'Error',
            onError = null,
        } = options;

        // Log to console in development
        if (import.meta.env.DEV) {
            console.error('Error:', error);
        }

        // If it's an axios error, use the specific handler
        if (error.response || error.request) {
            return handleAxiosError(error, options);
        }

        // Handle as general error
        const message = error?.message || error?.toString() || 'An unexpected error occurred';

        if (showToast) {
            toast.error(message, title, { duration: 7000 });
        }

        // Call custom error handler if provided
        if (onError) {
            onError(error);
        }
    };

    /**
     * Handle promise rejection
     * @param {Promise} promise - Promise to handle
     * @param {Object} options - Handler options
     * @returns {Promise} - Original promise
     */
    const handlePromise = async (promise, options = {}) => {
        try {
            return await promise;
        } catch (error) {
            handleError(error, options);
            throw error; // Re-throw so caller can handle if needed
        }
    };

    /**
     * Wrap an async function with error handling
     * @param {Function} fn - Async function to wrap
     * @param {Object} options - Handler options
     * @returns {Function} - Wrapped function
     */
    const wrapAsync = (fn, options = {}) => {
        return async (...args) => {
            try {
                return await fn(...args);
            } catch (error) {
                handleError(error, options);
                throw error;
            }
        };
    };

    return {
        handleError,
        handleAxiosError,
        handlePromise,
        wrapAsync,
    };
}

/**
 * Global error event listener setup
 * Call this once in your app.js to catch unhandled errors
 */
export function setupGlobalErrorHandling() {
    const { handleError } = useErrorHandler();

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        event.preventDefault();
        handleError(event.reason, {
            title: 'Unhandled Error',
            showToast: true,
        });
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
        // Don't show toast for resource loading errors (images, scripts, etc.)
        if (event.target !== window) {
            return;
        }

        handleError(event.error || event.message, {
            title: 'Application Error',
            showToast: true,
        });
    });
}
